import React, { useState, useContext } from "react";
import { NavLink, Routes, Route, useNavigate } from "react-router-dom";
import { logout } from "../../utils/auth";
import {
  LayoutDashboard,
  Bell,
  FileBarChart2,
  Camera,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Shield,
  Eye,
  HardDrive,
} from "lucide-react";

import Dashboard from "./dashboard";
import Alerts from "./alerts";
import Reports from "./reports";
import CameraView from "./cameraview";
import Configure from "./configure";
import PersonCount from "./personcount";
import NVR from "./nvr";
import { Navbar } from "@material-tailwind/react";
import { RootContext } from "../../context/RootContext";

const navItems = [
  {
    label: "Dashboard",
    icon: LayoutDashboard,
    path: "/tablespacehome",
    exact: true,
  },
  {
    label: "Alerts",
    icon: Bell,
    path: "/tablespacehome/alerts",
  },
  {
    label: "Reports",
    icon: FileBarChart2,
    path: "/tablespacehome/reports",
  },
  {
    label: "Person Count",
    icon: Shield,
    path: "/tablespacehome/personcount",
  },
  {
    label: "Cameras",
    icon: Camera,
    path: "/tablespacehome/cameras",
  },
  {
    label: "Configure",
    icon: Eye,
    path: "/tablespacehome/configure",
  },
  {
    label: "NVR",
    icon: HardDrive,
    path: "/tablespacehome/nvr",
  }
];

const Layout = () => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const { dispatch } = useContext(RootContext);

  const handleLogout = async () => {
    await logout();
    dispatch({ type: "LOGOUT" });
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 font-sans">
      {/* ── Top Bar ── */}
      <header className="sticky top-0 z-50 h-[60px] flex items-center px-6 backdrop-blur-xl bg-white/95 border-b border-slate-200">
        {/* Logo */}
        <div className="flex items-center shrink-0">

        {/* Logo Image */}
        <div className="flex items-center justify-center">
            <img
            src="/logo.png"
            alt="Logo"
            className="h-[42px] w-auto object-contain"
            />
        </div>

        {/* Divider Space */}
        <div className="mx-5 h-8 w-px bg-slate-200" />

        {/* Platform Name */}
        <div className="flex flex-col">
            <span className="text-[15px] font-bold tracking-[-0.3px] text-slate-900">
            CogniVision
            </span>

            <span className="text-[10px] font-semibold tracking-[0.18em] text-slate-500 uppercase">
            Next Gen Video Analytics Platform
            </span>
        </div>
        </div>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className="ml-auto flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-[12px] font-semibold text-red-600 hover:bg-red-100 transition-all"
        >
          <LogOut size={14} />
          <span>Logout</span>
        </button>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* ── Sidebar ── */}
        <aside
          className={`fixed top-[60px] left-0 z-40 h-[calc(100vh-60px)] bg-white/95 border-r border-slate-200 flex flex-col transition-all duration-300 ${
            collapsed ? "w-16" : "w-[220px]"
          }`}
        >
          {/* Collapse Toggle */}
          <div
            className={`flex ${
              collapsed ? "justify-center" : "justify-end"
            } px-3 pt-3 pb-1`}
          >
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="w-7 h-7 rounded-md border border-slate-200 bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-slate-200 transition-all"
            >
              {collapsed ? (
                <ChevronRight size={14} />
              ) : (
                <ChevronLeft size={14} />
              )}
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-2 flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.exact}
                className={({ isActive }) =>
                  `
                  flex items-center
                  ${collapsed ? "justify-center" : "justify-start"}
                  ${collapsed ? "gap-0" : "gap-2.5"}
                  px-3 py-2.5 rounded-[10px]
                  text-[13px] font-semibold
                  transition-all duration-150
                  border
                  ${
                    isActive
                      ? "bg-blue-50 text-blue-600 border-blue-100"
                      : "text-slate-500 border-transparent hover:bg-slate-100"
                  }
                `
                }
              >
                {({ isActive }) => (
                  <>
                    <item.icon
                      size={17}
                      color={isActive ? "#2563eb" : "#64748b"}
                    />

                    {!collapsed && <span>{item.label}</span>}
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* ── Main Content ── */}
        <main
          className={`flex-1 overflow-y-auto p-7 min-h-[calc(100vh-60px)] bg-slate-50 transition-all duration-300 ${
            collapsed ? "ml-16" : "ml-[220px]"
          }`}
        >
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="alerts" element={<Alerts />} />
            <Route path="reports" element={<Reports />} />
            <Route path="cameras" element={<CameraView />} />
            <Route path="configure" element={<Configure />} />
            <Route path="personcount" element={<PersonCount />} />
            <Route path="nvr" element={<NVR />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default Layout;